import { useEffect, useRef, useState } from 'react'
import Debug from 'debug'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import { useBrandName } from '@jotta/grpc-connect-client/config'
import { Button } from '@jotta/ui/Button'
import { Flex } from '@jotta/ui/Flex'
import { Input } from '@jotta/ui/Input'
import { RadixDialog } from '@jotta/ui/RadixDialog'
import type { RadixModalProps } from '@jotta/ui/RadixModal'
import { RadixModal } from '@jotta/ui/RadixModal'

const debug = Debug('jotta:files:action:RenameDialog')

type DeleteDeviceDialogProps = {
  onConfirm: () => void
  deviceName: string
} & RadixModalProps

export const DeleteDeviceDialog = ({
  onConfirm,
  open,
  onClose,
  deviceName,
  ...props
}: DeleteDeviceDialogProps) => {
  const [challengeInput, setChallengeInput] = useState('')
  const deleteButton = useRef<HTMLButtonElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const challengeAccepted = challengeInput.trim() === deviceName.trim()
  const brandName = useBrandName()

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  useEffect(() => {
    if (challengeAccepted && deleteButton.current) {
      deleteButton.current.focus()
    }
  }, [challengeAccepted])

  return (
    <RadixModal open={open} onClose={onClose} {...props}>
      <RadixDialog
        data-testid="DeleteDeviceDialog"
        title={t`Confirm delete device`.toUpperCase()}
        onClose={onClose}
      >
        <h1 sx={{ fontSize: 5 }}>{deviceName}</h1>
        <p sx={{ mt: 2, mb: 0, fontSize: 2 }}>
          <Trans>
            You are about to permanently delete <strong>{deviceName}</strong>{' '}
            from your {brandName} account.
          </Trans>
        </p>
        <p sx={{ mt: 2, mb: 0, fontSize: 2 }}>
          <Trans>
            Please uninstall {brandName} from the device before you proceed.
          </Trans>
        </p>
        <p sx={{ mt: 2, mb: 0, fontSize: 2 }}>
          <Trans>
            All files backed up by <strong>{deviceName}</strong> will be
            permanently deleted from your account.
          </Trans>
        </p>
        <p sx={{ mt: 2, mb: 0, fontSize: 2 }}>
          <Trans>
            The change is permanent and can not be undone. Are you sure you want
            to continue?
          </Trans>
        </p>
        <div sx={{ mt: 5 }}>
          <p sx={{ margin: 0 }}>
            <Trans>
              Please type <span sx={{ color: 'red' }}>{deviceName}</span> below
              to confirm
            </Trans>
            :
          </p>
          <Input
            ref={inputRef}
            value={challengeInput}
            onChange={e => setChallengeInput(e.target.value)}
            placeholder={t`Device Name`}
            sx={{ mt: 2, fontSize: 4 }}
          />
        </div>
        <Flex sx={{ gap: 3, justifyContent: 'flex-end', mt: 4, mb: 2 }}>
          <Button
            className="btn btn-outline"
            onClick={() => onClose && onClose(false)}
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button
            ref={deleteButton}
            className="btn btn-destructive"
            onClick={() => {
              onConfirm()
            }}
            disabled={!challengeAccepted}
          >
            <Trans>Delete</Trans>
          </Button>
        </Flex>
      </RadixDialog>
    </RadixModal>
  )
}
